/* useCaptainWorkspaceConfig — caller's effective Captain role + capabilities,
 * served from `GET /coreleads/captain/config/me`.
 *
 * Phase 5 / T31. The endpoint ships in BE PR #1556. Until that merges the
 * hook still works: any non-2xx (auth or otherwise) falls back to a
 * gate-closed minimal-permissions config so the UI degrades to a clean
 * empty state rather than crashing or — worse — leaking actions a role
 * shouldn't see.
 *
 * **The server is the security boundary.** `filterToolsByRole` on the BE
 * already blocks every tool call that the role doesn't permit. This hook
 * only governs which UI affordances render — never rely on it as the only
 * check, and never call it from anywhere outside a React tree (it's a
 * `useQuery`, not a fetcher). */

import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/hooks/useAuth';
import { apiClient } from '@/shared/lib/api/client';
import type {
  Capability,
  CaptainRole,
  CaptainWorkspaceConfigResponse,
  ToolScope,
} from '../types';

export const CAPTAIN_WORKSPACE_CONFIG_KEY = ['captain', 'workspace-config', 'me'] as const;

const ME_PATH = '/coreleads/captain/config/me';

/* Five-minute staleTime — role assignments are workspace-admin actions and
 * almost never change mid-session. We trade a 5-min lag for fewer round
 * trips on every navigation. The cache is keyed on the constant tuple
 * above (no userId suffix) because the auth client already scopes the
 * caller via the bearer token + `gamix-app-id`; a different login lands on
 * a fresh QueryClient anyway via the auth bootstrap path. */
const STALE_TIME_MS = 5 * 60 * 1000;

/* Gate-closed fallback used on auth errors (401 / 403) and any other non-2xx
 * response. Empty `capabilities` means every `<Capability>` mismatches and
 * renders its `fallback` (or nothing); empty `tools` means every
 * `useHasTool` returns false. The role label is set to `sales_rep` purely
 * as a placeholder — no UI should display it because no capability check
 * will pass. Anything that included a capability here would defeat the
 * safe default. */
export const FALLBACK_WORKSPACE_CONFIG: CaptainWorkspaceConfigResponse = {
  role: 'sales_rep',
  capabilities: [],
  toolScope: 'mine',
  tools: [],
  kbCategories: [],
};

async function fetchCaptainConfigMe(): Promise<CaptainWorkspaceConfigResponse> {
  /* `apiClient` (axios) handles the bearer token, `gamix-app-id` header, and
   * the 401 refresh dance — same pattern as `sessions-api.ts`. The
   * generated `@coretava/api` client doesn't yet expose `/captain/config/me`
   * (BE PR #1556 is open at the time of writing); swap to codegen once it
   * lands. */
  const { data } = await apiClient.get<CaptainWorkspaceConfigResponse>(ME_PATH);
  return data;
}

export type UseCaptainWorkspaceConfigReturn = {
  role: CaptainRole;
  capabilities: Capability[];
  toolScope: ToolScope;
  tools: string[] | 'all';
  kbCategories: string[] | 'all';
  isLoading: boolean;
  /* True when the query has resolved with a real BE response (not the
   * gate-closed fallback). Use this when "no data" needs to be distinct
   * from "user truly lacks the capability" — e.g. role-based redirects
   * that should stay put while the config is uncertain. */
  hasResolvedData: boolean;
};

export function useCaptainWorkspaceConfig(): UseCaptainWorkspaceConfigReturn {
  const userId = useAuthStore((s) => s.user?.id) ?? null;

  const query = useQuery<CaptainWorkspaceConfigResponse, Error>({
    queryKey: CAPTAIN_WORKSPACE_CONFIG_KEY,
    queryFn: fetchCaptainConfigMe,
    staleTime: STALE_TIME_MS,
    /* Pre-bootstrap (login redirect) — leave the cache empty so the
     * fallback below kicks in. This mirrors `useCaptainSessions`. */
    enabled: Boolean(userId),
    /* On 401 / 403 we don't retry — the api client refreshes and rotates
     * the token in its interceptor; a real auth failure means the caller
     * truly lacks access and the fallback is correct. Other transient
     * errors get one retry. */
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 1;
    },
  });

  /* `query.data` is undefined while loading and on error. Fall back to the
   * gate-closed minimal config in both states so consumers never have to
   * guard against `undefined`. The `isLoading` flag is forwarded so
   * `<Capability>` can render nothing instead of the fallback during the
   * initial fetch (avoiding the closed -> open flicker). `hasResolvedData`
   * is forwarded for callers that need to distinguish the fallback from a
   * real BE response (e.g. role-based redirects). */
  const data = query.data ?? FALLBACK_WORKSPACE_CONFIG;

  return {
    role: data.role,
    capabilities: data.capabilities,
    toolScope: data.toolScope,
    tools: data.tools,
    kbCategories: data.kbCategories,
    isLoading: query.isLoading,
    hasResolvedData: query.data !== undefined,
  };
}
